import { useRef, useState } from "react";

export type TooltipPos = { x: number; y: number; text: string };

export function useDelayedTooltip<T extends HTMLElement = HTMLElement>(options: {
    getText: (el: T) => string | null;
    delay?: number;
}) {
    const { getText, delay = 1000 } = options;
    const ref = useRef<T>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [visible, setVisible] = useState<{ x: number; y: number } | null>(null);

    // Re-derive text at render time so the tooltip stays in sync with parent state changes
    // (e.g. clicking the visibility button while the tooltip is already showing).
    const pos: TooltipPos | null = (() => {
        if (!visible || !ref.current) {
            return null;
        }
        const text = getText(ref.current);
        return text !== null ? { ...visible, text } : null;
    })();

    const onMouseEnter = () => {
        timerRef.current = setTimeout(() => {
            const el = ref.current;
            if (!el) {
                return;
            }
            const text = getText(el);
            if (text === null) {
                return;
            }
            const rect = el.getBoundingClientRect();
            setVisible({ x: rect.left + rect.width / 2, y: rect.top - 4 });
        }, delay);
    };

    const onMouseLeave = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        setVisible(null);
    };

    return { ref, pos, onMouseEnter, onMouseLeave };
}
